import { isThereStripeConfiguration } from '@eventbrite/payments';
import {
    StripeElementsAustraliaExperimentVariant,
    StripeElementsCanadaExperimentVariant,
    StripeElementsEuroExperimentVariant,
    StripeElementsNewZealandExperimentVariant,
    StripeElementsUKExperimentVariant,
    StripeElementsUnitedStatesExperimentVariant,
} from '../../constants/statsig/types';
import {
    getAreEventbriteLegacyPaymentMethodsDisabled,
    getIsAppRebrandActive,
    getIsProd,
    getIsStripeElementsInMobileAppEnabled,
    isAndroidApp,
    isEventbriteTLD,
    isNativeApp,
    isPayinsFlow,
} from '../../selectors/app';
import { getIsTransferredOrder } from '../../selectors/order/getIsTransferredOrder';
import {
    getCurrencyFromOrder,
    isAddAttendeesOrder,
} from '../../selectors/order/typedOrderSelectors';
import { CheckoutState } from '../../state';
import { getNativeAppVersion } from '../../utils/browser';
import { compareVersions } from '../../utils/compareVersions';
import {
    getStripeElementsAustraliaExperimentValue,
    getStripeElementsCanadaExperimentValue,
    getStripeElementsEuroExperimentValue,
    getStripeElementsNewZealandExperimentValue,
    getStripeElementsUKExperimentValue,
    getStripeElementsUnitedStatesExperimentValue,
} from '../../utils/statsig';
import { isStripeConfirmationTokenPaymentMethodAvailable } from './isStripeConfirmationTokenPaymentMethodAvailable';

const MINIMUM_ANDROID_APP_VERSION_FOR_STRIPE_ELEMENTS = '10.28.0';

type DecisionData = Record<string, unknown>;

const printDecisionData = (decisionData: DecisionData) => {
    let output = `
    shouldStripeElementsBeAvailable:
`;
    Object.entries(decisionData).forEach(([key, value]) => {
        output += `      ${key}: ${value},
`;
    });

    console.log(output);
};

export const shouldStripeElementsBeAvailable = (state: CheckoutState) => {
    const decisionData: DecisionData = {};
    decisionData.isNewExperienceEnabled =
        getStripeElementsAustraliaExperimentValue() ===
            StripeElementsAustraliaExperimentVariant.newExperience ||
        getStripeElementsUnitedStatesExperimentValue() ===
            StripeElementsUnitedStatesExperimentVariant.newExperience ||
        getStripeElementsNewZealandExperimentValue() ===
            StripeElementsNewZealandExperimentVariant.newExperience ||
        getStripeElementsCanadaExperimentValue() ===
            StripeElementsCanadaExperimentVariant.newExperience ||
        getStripeElementsUKExperimentValue() ===
            StripeElementsUKExperimentVariant.newExperience ||
        getStripeElementsEuroExperimentValue() ===
            StripeElementsEuroExperimentVariant.newExperience;

    decisionData.isCreatorFlow =
        (decisionData.isAddAttendeesOrder = isAddAttendeesOrder(state)) ||
        (decisionData.isPayinsFlow = isPayinsFlow(state)) ||
        (decisionData.isTransferredOrder = getIsTransferredOrder(state));

    decisionData.areEventbriteLegacyPaymentMethodsDisabled =
        getAreEventbriteLegacyPaymentMethodsDisabled(state);

    decisionData.applicableByContainer = isApplicableByContainer(
        state,
        decisionData,
    );

    decisionData.enabledAccordingToDeviceAndFeatureFlags =
        decisionData.areEventbriteLegacyPaymentMethodsDisabled ||
        (decisionData.isNewExperienceEnabled &&
            decisionData.applicableByContainer);

    decisionData.isEventbriteTLD = isEventbriteTLD(state);
    decisionData.shouldEnableStripeElementsAccordingToFlow =
        decisionData.enabledAccordingToDeviceAndFeatureFlags &&
        !decisionData.isCreatorFlow &&
        (decisionData.isEventbriteTLD || isNativeApp(state));

    decisionData.orderCurrency = getCurrencyFromOrder(state);
    decisionData.isThereStripeConfig = isThereStripeConfiguration(
        decisionData.orderCurrency as string,
        getIsProd(state),
    );
    decisionData.stripeConfirmationTokenPaymentMethodAvailable =
        isStripeConfirmationTokenPaymentMethodAvailable(state);
    decisionData.shouldStripeElementsBeAvailable =
        decisionData.shouldEnableStripeElementsAccordingToFlow &&
        decisionData.stripeConfirmationTokenPaymentMethodAvailable &&
        decisionData.isThereStripeConfig;

    if (!getIsProd(state)) {
        printDecisionData(decisionData);
    }

    return decisionData.shouldStripeElementsBeAvailable;
};

const isCurrentVersionGreaterThanOrEqualToMinimum = (
    currentVersion: string,
): boolean => {
    if (currentVersion === '') {
        return true;
    }

    return (
        compareVersions(
            currentVersion,
            MINIMUM_ANDROID_APP_VERSION_FOR_STRIPE_ELEMENTS,
        ) >= 0
    );
};

const isApplicableByContainer = (
    state: CheckoutState,
    decisionData: DecisionData,
) => {
    decisionData.nativeApp = isNativeApp(state);
    decisionData.rebrandActive = getIsAppRebrandActive(state);
    decisionData.applicableByContainer = !decisionData.rebrandActive;

    if (decisionData.nativeApp) {
        decisionData.currentVersion = getNativeAppVersion();
        decisionData.androidApp = isAndroidApp(state);
        decisionData.stripeElementsInMobileAppEnabled =
            getIsStripeElementsInMobileAppEnabled(state);

        decisionData.applicableByContainer =
            (decisionData.stripeElementsInMobileAppEnabled as boolean) &&
            (!decisionData.androidApp ||
                (decisionData.isCurrentVersionGreaterThanOrEqualToMinimum =
                    isCurrentVersionGreaterThanOrEqualToMinimum(
                        decisionData.currentVersion as string,
                    )));
    }

    return decisionData.applicableByContainer;
};
